import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  getAuth, 
  confirmPasswordReset,
  verifyPasswordResetCode
} from 'firebase/auth';
import './Login.css'; // Using the same CSS as Login

const PasswordReset = () => {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [validCode, setValidCode] = useState(false);
  const [verifying, setVerifying] = useState(true);

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const auth = getAuth();

  // Get the action code from URL parameters
  const actionCode = searchParams.get('oobCode');
  const mode = searchParams.get('mode');

  useEffect(() => {
    const verifyResetCode = async () => {
      if (!actionCode || mode !== 'resetPassword') {
        setError('Invalid or expired password reset link.');
        setVerifying(false);
        return;
      }

      try {
        // Verify the password reset code and get the user's email
        const userEmail = await verifyPasswordResetCode(auth, actionCode);
        setEmail(userEmail);
        setValidCode(true);
      } catch (error) {
        console.error('Password reset code verification failed:', error);
        switch (error.code) {
          case 'auth/expired-action-code':
            setError('This password reset link has expired. Please request a new one.');
            break;
          case 'auth/invalid-action-code':
            setError('This password reset link is invalid. Please request a new one.');
            break;
          case 'auth/user-disabled':
            setError('This account has been disabled.');
            break;
          default:
            setError('Invalid password reset link. Please request a new one.');
        }
      } finally {
        setVerifying(false);
      }
    };

    verifyResetCode();
  }, [actionCode, mode, auth]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    // Validation
    if (!password || !confirmPassword) {
      setError('Please fill in all fields');
      setLoading(false);
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
      // Confirm the password reset
      await confirmPasswordReset(auth, actionCode, password);
      
      setSuccess({
        title: "🎉 Password Reset Successful!",
        message: "Your password has been updated successfully.",
        details: "You can now sign in with your new password. Redirecting to login page..."
      });

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/login', { 
          state: { 
            message: 'Password updated successfully! Please sign in with your new password.',
            email: email // Pre-fill email on login page
          }
        });
      }, 3000);

    } catch (error) {
      console.error('Password reset failed:', error);
      
      switch (error.code) {
        case 'auth/expired-action-code':
          setError('This password reset link has expired. Please request a new one.');
          break;
        case 'auth/invalid-action-code':
          setError('This password reset link is invalid. Please request a new one.');
          break;
        case 'auth/weak-password':
          setError('Password is too weak. Please choose a stronger password.');
          break;
        default:
          setError('Failed to reset password. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const goToLogin = () => {
    navigate('/login');
  };

  if (verifying) {
    return (
      <div className="modern-login-container">
        {/* Top-left logo */}
        <div className="login-logo">
          <img
            src="/icons/Nezo_Logo_White.png"
            alt="Nezo Ops Console"
          />
        </div>

        {/* Back button — top-right on green background */}
        <a href="https://app.nezo-app.com/" className="back-button-login">
          <img
            src="/icons/Nezo_Navy_Fabricon.png"
            alt="Back to Nezo"
            style={{ height: 38, width: 'auto' }}
          />
        </a>

        <div className="login-form-section">
          <div className="form-container">
            <div className="form-header">
              <h2>Verifying Reset Link</h2>
              <p className="form-subtitle">Please wait while we verify your password reset link...</p>
            </div>
            <div style={{ textAlign: 'center', padding: '40px 0' }}>
              <div className="spinner" style={{ margin: '0 auto' }}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modern-login-container">
      {/* Top-left logo */}
      <div className="login-logo">
        <img
          src="/icons/Nezo_Logo_White.png"
          alt="Nezo Ops Console"
        />
      </div>

      {/* Back button — top-right on green background */}
      <a href="https://app.nezo-app.com/" className="back-button-login">
        <img
          src="/icons/Nezo_Navy_Fabricon.png"
          alt="Back to Nezo"
          style={{ height: 38, width: 'auto' }}
        />
      </a>

      {/* Password Reset Form */}
      <div className="login-form-section">
        <div className="form-container">
          <div className="form-header">
            <h2>{validCode ? 'Reset Your Password' : 'Reset Link Invalid'}</h2>
            <p className="form-subtitle">
              {validCode 
                ? `Enter a new password for ${email}` 
                : 'Please request a new password reset link'
              }
            </p>
          </div>

          {error && (
            <div className="alert alert-error">
              <div className="alert-icon">⚠️</div>
              <span>{error}</span>
            </div>
          )}

          {success && (
            <div className="alert alert-success">
              <div className="alert-icon">✅</div>
              <div className="alert-content">
                <div className="success-title">{success.title}</div>
                <div className="success-details">
                  {success.message}
                  <br />
                  <br />
                  {success.details}
                </div>
              </div>
            </div>
          )}

          {validCode ? (
            <form className="modern-form" onSubmit={handlePasswordReset}>
              <div className="form-group">
                <label htmlFor="password" className="form-label">New Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="9" width="14" height="9" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M7 9V5a3 3 0 0 1 6 0v4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <input
                    type={showPassword ? "text" : "password"}
                    id="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="form-input"
                    placeholder="Enter your new password"
                    required
                    disabled={loading}
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.12 14.12C12.95 15.04 11.49 15.5 10 15.5C4.5 15.5 1.5 10 1.5 10C2.73 7.74 4.53 5.82 6.88 4.88M8.21 3.73C8.8 3.6 9.4 3.5 10 3.5C15.5 3.5 18.5 10 18.5 10C17.89 11.17 17.09 12.18 16.17 13M12.12 7.88C12.49 8.25 12.74 8.74 12.74 9.26C12.74 10.35 11.85 11.24 10.76 11.24C10.24 11.24 9.75 10.99 9.38 10.62" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1 1L19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M1 10S4 4 10 4S19 10 19 10S16 16 10 16S1 10 1 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="confirmPassword" className="form-label">Confirm New Password</label>
                <div className="input-wrapper">
                  <div className="input-icon">
                    <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <rect x="3" y="9" width="14" height="9" rx="2" ry="2" stroke="currentColor" strokeWidth="2"/>
                      <path d="M7 9V5a3 3 0 0 1 6 0v4" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                  </div>
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="form-input"
                    placeholder="Confirm your new password"
                    required
                    disabled={loading}
                    minLength="6"
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M14.12 14.12C12.95 15.04 11.49 15.5 10 15.5C4.5 15.5 1.5 10 1.5 10C2.73 7.74 4.53 5.82 6.88 4.88M8.21 3.73C8.8 3.6 9.4 3.5 10 3.5C15.5 3.5 18.5 10 18.5 10C17.89 11.17 17.09 12.18 16.17 13M12.12 7.88C12.49 8.25 12.74 8.74 12.74 9.26C12.74 10.35 11.85 11.24 10.76 11.24C10.24 11.24 9.75 10.99 9.38 10.62" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M1 1L19 19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                        <path d="M1 10S4 4 10 4S19 10 19 10S16 16 10 16S1 10 1 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <circle cx="10" cy="10" r="3" stroke="currentColor" strokeWidth="2"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button 
                type="submit" 
                className="form-submit-btn"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <div className="spinner"></div>
                    Resetting Password...
                  </>
                ) : (
                  'Reset Password'
                )}
              </button>
            </form>
          ) : (
            <div className="form-container" style={{ textAlign: 'center' }}>
              <button 
                type="button"
                onClick={goToLogin}
                className="form-submit-btn"
              >
                Back to Login
              </button>
            </div>
          )}

          <div className="form-footer">
            <p>
              Remember your password? {' '}
              <button 
                type="button"
                onClick={goToLogin}
                className="switch-mode-btn"
                disabled={loading}
              >
                Sign in instead
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PasswordReset;
